// src/components/VoiceDictation.jsx
// Bloc de dictée vocale réutilisable, moderne et explicite.
// L'utilisateur décrit sa demande à voix haute → l'IA remplit le formulaire.
// Reçoit l'objet `stt` (hook useSpeechToText) et un callback `onAutofill`.
// NB: le métrage micro (useMicLevel) reste appelé côté page ; ici on ne fait
// qu'afficher le WaveformVisualizer piloté par `stt.listening`.
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Square, Trash2, Sparkles, Wand2, AlertTriangle } from "lucide-react";
import WaveformVisualizer from "./WaveformVisualizer";
import settingsService from "../services/settingsService";

export default function VoiceDictation({
  stt,
  onAutofill,
  busy = false,
  title = "Décrivez votre demande à voix haute",
  subtitle = "Parlez naturellement — l'IA transcrit puis remplit le formulaire pour vous.",
  hint,
  autofillLabel = "Remplir le formulaire avec l'IA",
  placeholder = "Votre demande s'affichera ici au fur et à mesure que vous parlez…",
}) {
  // Fonctionnalités IA activées par l'admin ? Masqué par défaut tant que non chargé/activé.
  const [aiEnabled, setAiEnabled] = useState(null);
  useEffect(() => {
    let mounted = true;
    settingsService.getAiEnabled().then((v) => {
      if (mounted) setAiEnabled(v);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const listening = !!stt?.listening;
  const text = ((stt?.transcript || "") + (stt?.interim ? " " + stt.interim : "")).trim();
  const hasText = text.length > 0;

  // Tant que l'admin n'a pas activé l'IA (défaut), on ne rend rien du tout.
  if (aiEnabled !== true) return null;

  // Navigateur non compatible : message clair.
  if (stt && stt.supported === false) {
    return (
      <div className="mb-6 rounded-3xl border border-amber-200 bg-amber-50 p-4 sm:p-5">
        <div className="flex items-start gap-3 text-amber-900">
          <AlertTriangle className="w-5 h-5 flex-none mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold">La dictée vocale n'est pas disponible sur ce navigateur.</p>
            <p className="opacity-80">Utilisez Chrome ou Edge — ou remplissez le formulaire manuellement ci-dessous.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-3xl border border-orange-100 bg-gradient-to-br from-[#fff8f1] to-white p-5 sm:p-6 shadow-sm">
      {/* En-tête : badge IA + explication de l'intérêt */}
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-600/10 text-orange-700 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1">
          <Sparkles className="w-3.5 h-3.5" />
          Nouzha AI
        </span>
      </div>

      <h3 className="text-lg sm:text-xl font-black text-gray-900 leading-tight">{title}</h3>
      <p className="mt-1 text-sm text-gray-600">{subtitle}</p>

      {/* Zone micro : gros bouton clair + état + onde */}
      <div className="mt-5 flex flex-col items-center gap-3">
        <div className="relative">
          {/* halo pulsant quand on écoute */}
          <AnimatePresence>
            {listening && (
              <motion.span
                initial={{ opacity: 0.6, scale: 1 }}
                animate={{ opacity: 0, scale: 1.8 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.2, repeat: Infinity, ease: "easeOut" }}
                className="absolute inset-0 rounded-full bg-orange-500"
              />
            )}
          </AnimatePresence>
          <motion.button
            type="button"
            whileTap={{ scale: 0.92 }}
            onClick={() => (listening ? stt?.stop?.() : stt?.start?.())}
            aria-pressed={listening}
            aria-label={listening ? "Arrêter la dictée" : "Démarrer la dictée"}
            className={`relative z-10 flex items-center justify-center w-20 h-20 rounded-full shadow-lg transition-colors focus:outline-none focus:ring-4 ${
              listening
                ? "bg-red-500 text-white focus:ring-red-200"
                : "bg-gradient-to-br from-orange-500 to-orange-600 text-white focus:ring-orange-200"
            }`}
          >
            {listening ? <Square className="w-7 h-7" /> : <Mic className="w-8 h-8" />}
          </motion.button>
        </div>

        <div className="h-10 flex items-center justify-center">
          {listening ? (
            <div className="flex items-center gap-3">
              <WaveformVisualizer active={listening} width={160} height={36} />
              <span className="text-sm font-semibold text-red-600 animate-pulse">Écoute… parlez</span>
            </div>
          ) : (
            <span className="text-sm font-semibold text-gray-500">
              {hasText ? "Appuyez pour continuer la dictée" : "Appuyez sur le micro et parlez"}
            </span>
          )}
        </div>
      </div>

      {/* Transcription */}
      <div className="mt-4 relative">
        <textarea
          value={text}
          readOnly
          rows={3}
          placeholder={placeholder}
          className="w-full resize-none border-2 border-orange-200 rounded-2xl px-4 py-3 bg-white text-gray-800 placeholder-gray-400 outline-none focus:border-orange-400"
        />
        {hasText && (
          <button
            type="button"
            onClick={() => stt?.reset?.()}
            className="absolute top-2 right-2 inline-flex items-center gap-1 text-xs font-semibold text-gray-400 hover:text-red-500 transition-colors"
            aria-label="Effacer la transcription"
          >
            <Trash2 className="w-3.5 h-3.5" /> Effacer
          </button>
        )}
      </div>

      {stt?.error && (
        <p className="mt-2 text-sm text-red-600">Une erreur est survenue avec la dictée. Réessayez.</p>
      )}

      {/* CTA principal : remplir via IA */}
      {onAutofill && (
        <motion.button
          type="button"
          whileHover={{ scale: hasText && !busy ? 1.02 : 1 }}
          whileTap={{ scale: hasText && !busy ? 0.98 : 1 }}
          onClick={onAutofill}
          disabled={!hasText || busy}
          className={`mt-4 w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl font-black text-sm uppercase tracking-wide transition-all ${
            hasText && !busy
              ? "bg-[#0f172a] text-white hover:bg-[#1e293b] shadow-lg"
              : "bg-gray-200 text-gray-400 cursor-not-allowed"
          }`}
        >
          {busy ? (
            <>
              <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
              Analyse en cours…
            </>
          ) : (
            <>
              <Wand2 className="w-4 h-4" />
              {autofillLabel}
            </>
          )}
        </motion.button>
      )}

      {hint && <p className="mt-3 text-xs text-gray-500 text-center">{hint}</p>}
    </div>
  );
}
